import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, X, MessageCircle, MapPin, Clock, Star, AlertCircle, CheckCircle, XCircle, Hourglass } from 'lucide-react';
import UpgradeModal from './UpgradeModal';

const MatchingInterface = () => {
  const navigate = useNavigate();
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userMatches, setUserMatches] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userTier, setUserTier] = useState('free');
  
  // Use ref for notification to bypass React re-render issues
  const notificationRef = useRef(null);

  useEffect(() => {
    fetchPotentialMatches();
    fetchUserMatches();
    fetchUserTier();
  }, []);
  
  // Show custom notification using direct DOM manipulation with elegant glassmorphism
  const showNotification = (type, message, partnerName = '', position = null) => {
    console.log('🔔 Showing notification:', { type, message, partnerName, position });
    
    // Remove existing notification if any
    const existing = document.getElementById('queue-notification-modal');
    if (existing) {
      existing.remove();
    }
    
    // Create modal element directly
    const modal = document.createElement('div');
    modal.id = 'queue-notification-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 27, 75, 0.92));
      backdrop-filter: blur(20px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      padding: 20px;
    `;
    
    // For queue notifications (info type with position), use the elegant glassmorphism design
    if (type === 'info' && position !== null) {
      modal.innerHTML = `
        <div style="width: 100%; max-width: 28rem;">
          <div style="
            backdrop-filter: blur(60px);
            background: linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%);
            border-radius: 2rem;
            padding: 2.5rem;
            border: 1px solid rgba(255,255,255,0.1);
            box-shadow: 0 25px 70px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          ">
            <div style="text-align: center;">
              
              <!-- Hourglass Icon -->
              <div style="display: inline-flex; align-items: center; justify-content: center; margin-bottom: 1.5rem;">
                <div style="width: 5rem; height: 5rem; border-radius: 50%; border: 2px solid rgba(244, 114, 182, 0.2); position: relative;">
                  <div style="position: absolute; inset: 0.75rem; border-radius: 50%; background: linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(236, 72, 153, 0.15)); display: flex; align-items: center; justify-content: center;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(251, 207, 232, 0.8)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: rotate 2s linear infinite;">
                      <path d="M5 22h14"/>
                      <path d="M5 2h14"/>
                      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/>
                      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              <!-- Title -->
              <div style="margin-bottom: 1.5rem;">
                <h2 style="font-size: 1.5rem; font-weight: 300; color: white; letter-spacing: 0.025em; margin-bottom: 0.5rem;">
                  Currently Unavailable
                </h2>
                <p style="color: rgba(255,255,255,0.6); font-size: 1rem; font-weight: 300; line-height: 1.625;">
                  ${partnerName} is in another date right now
                </p>
              </div>
              
              <!-- Queue Position Badge -->
              <div style="
                backdrop-filter: blur(16px);
                background: linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
                border-radius: 0.75rem;
                padding: 1rem;
                border: 1px solid rgba(255,255,255,0.06);
                margin-bottom: 1.5rem;
              ">
                <p style="color: rgba(255,255,255,0.4); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.25rem;">Your Position</p>
                <p style="color: white; font-size: 3rem; font-weight: 300; line-height: 1;">#${position}</p>
                <p style="color: rgba(255,255,255,0.5); font-size: 0.875rem; margin-top: 0.5rem;">We'll notify you when they're available</p>
              </div>
              
              <!-- Action Buttons -->
              <div style="display: flex; flex-direction: column; gap: 0.75rem; padding-top: 0.5rem;">
                <button id="queue-notification-close" style="
                  width: 100%;
                  position: relative;
                  overflow: hidden;
                  border-radius: 0.75rem;
                  background: linear-gradient(135deg, rgba(236, 72, 153, 0.85), rgba(239, 68, 68, 0.85));
                  box-shadow: 0 8px 24px rgba(236, 72, 153, 0.2);
                  border: none;
                  padding: 0.875rem;
                  color: white;
                  font-weight: 500;
                  letter-spacing: 0.025em;
                  cursor: pointer;
                  font-size: 1rem;
                  transition: all 0.3s ease;
                ">
                  <span style="position: relative; z-index: 1;">Got It</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <style>
          @keyframes rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(180deg); }
          }
          #queue-notification-close:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(236, 72, 153, 0.3);
          }
        </style>
      `;
    } else {
      // For other notification types (error, success, basic info), use simple design
      const bgColor = type === 'info' ? '#3B82F6' : type === 'success' ? '#10B981' : '#EF4444';
      const emoji = type === 'info' ? '⏳' : type === 'success' ? '✅' : '❌';
      
      modal.innerHTML = `
        <div style="
          background-color: ${bgColor};
          color: white;
          padding: 32px;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
          text-align: center;
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">${emoji}</div>
          <p style="margin: 0 0 24px 0; font-size: 18px; line-height: 1.6; font-weight: 600;">
            ${message}
          </p>
          <button id="queue-notification-close" style="
            background-color: rgba(255,255,255,0.2);
            border: none;
            color: white;
            padding: 12px 32px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
          ">OK</button>
        </div>
      `;
    }
    
    document.body.appendChild(modal);
    console.log('✅ Modal appended to body');
    
    // Add click handler to close button
    const closeBtn = document.getElementById('queue-notification-close');
    if (closeBtn) {
      closeBtn.onclick = () => {
        console.log('🔔 Closing notification');
        modal.remove();
      };
    }
    
    // Close on background click
    modal.onclick = (e) => {
      if (e.target === modal) {
        console.log('🔔 Background clicked - closing');
        modal.remove();
      }
    };
  };

  const fetchPotentialMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      // Add timestamp to bypass cache
      const timestamp = Date.now();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matches?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPotentialMatches(data.matches || []);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      // Add timestamp to bypass cache
      const timestamp = Date.now();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-matches/confirmed?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserMatches(data.matches || []);
      }
    } catch (error) {
      console.error('Error fetching user matches:', error);
    }
  };


  const fetchUserTier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Determine tier based on is_vip and is_premium flags
        if (data.is_vip) {
          setUserTier('vip');
        } else if (data.is_premium) {
          setUserTier('premium');
        } else {
          setUserTier('free');
        }
      }
    } catch (error) {
      console.error('Error fetching user tier:', error);
      setUserTier('free'); // Default to free on error
    }
  };


  const handleLike = async (matchId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matches/${matchId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.is_match) {
          // Show match celebration
          alert('🎉 It\'s a Match! You can now start chatting!');
          
          // Refresh both potential matches and confirmed matches
          await fetchPotentialMatches(); // Refresh to remove the matched user
          await fetchUserMatches(); // Update confirmed matches
        } else {
          // Even if not a match, refresh potential matches to ensure fresh data
          await fetchPotentialMatches();
        }
        
        // Move to next potential match
        setCurrentMatchIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error liking user:', error);
    }
  };

  const handlePass = () => {
    // Just move to next match
    setCurrentMatchIndex(prev => prev + 1);
  };

  const startChat = async (userId) => {
    console.log('🔍 Start chat clicked. User tier:', userTier);
    
    // Check if user is free tier (frontend check)
    if (userTier === 'free') {
      console.log('🚫 Free user - showing upgrade modal');
      setShowUpgradeModal(true);
      console.log('✅ Modal state updated. showUpgradeModal should now be true');
      return;
    }
    
    console.log('✅ Premium/VIP user - proceeding to chat');
    
    try {
      // Find the match data to get match_id
      const matchData = userMatches.find(match => match.user.id === userId);
      if (!matchData || !matchData.match_id) {
        alert('Could not find match data');
        return;
      }
      
      const token = localStorage.getItem('token');
      // Get or create conversation for this match
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/match/${matchData.match_id}/conversation`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Navigate to the conversation
        navigate(`/chat/${data.conversation_id}`);
      } else if (response.status === 403) {
        // Backend confirmed user is free tier - show upgrade modal
        console.log('🚫 Backend blocked - free user confirmed, showing upgrade modal');
        setShowUpgradeModal(true);
      } else {
        alert('Failed to start chat. Please try again.');
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  const startSpeedDate = async (matchId) => {
    console.log('🎯 Starting speed date with matchId:', matchId);
    console.log('📊 Available userMatches:', userMatches);
    
    // Find the full match data
    const matchData = userMatches.find(match => match.user.id === matchId);
    console.log('🔍 Found matchData:', matchData);
    
    if (!matchData) {
      console.warn('❌ No match data found for matchId:', matchId);
      showNotification('error', 'Could not find match information. Please try again.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Use queue request endpoint
      console.log('📤 Requesting speed date via queue system...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          match_id: matchData.match_id
        })
      });
      
      const result = await response.json();
      console.log('📥 Queue request response:', result);
      
      // Check for email verification required error
      if (response.status === 403 && result.detail?.action === 'verify_email') {
        showNotification('error', '📧 Please verify your email to start speed dating. Check your inbox for the confirmation link.');
        return;
      }
      
      if (!response.ok) {
        throw new Error(result.detail?.message || result.detail || 'Failed to start speed date');
      }
      
      if (result.queued) {
        // User was queued - show elegant glassmorphism modal
        showNotification('info', `${matchData.user.name} is currently in a date. You're #${result.position} in queue. We'll notify you when they're available!`, matchData.user.name, result.position);
      } else if (result.requires_upgrade) {
        // Free user hit queue limit
        showNotification('info', result.message);
        setTimeout(() => navigate('/subscription/plans'), 2000);
      } else if (result.ready_to_start) {
        // Can start immediately
        console.log('🚀 Partner available, starting speed date...');
        navigate(`/speed-dating-demo?partner=${matchId}`);
      }
    } catch (error) {
      console.error('❌ Queue request error:', error);
      showNotification('error', error.message || 'Failed to request speed date. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  const currentMatch = potentialMatches[currentMatchIndex];

  if (!currentMatch || currentMatchIndex >= potentialMatches.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center max-w-md">
          <Heart className="h-16 w-16 text-white mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">You've Seen Everyone!</h2>
          <p className="text-white/80 mb-6">
            You've browsed all singles in your area. Check back later for new people!
          </p>
          <button
            onClick={() => navigate('/connections')}
            className="bg-white text-rose-500 px-6 py-3 rounded-full font-semibold hover:bg-rose-50 transition-colors flex items-center gap-2 mx-auto"
          >
            💚 View My Connections ({userMatches.length})
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-start justify-center p-3 sm:p-4 py-4 overflow-x-hidden">
      <div className="w-full max-w-sm sm:max-w-md">
        {/* Discovery Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Discover Singles</h1>
          <p className="text-white/90 text-sm sm:text-base">Browse potential matches near you</p>
        </div>
        
        {/* Match Card - Taller on mobile for better visibility */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden relative">
          {/* Photo - Compact height for better visibility of "View My Matches" */}
          <div className="h-[280px] sm:h-[340px] bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center relative overflow-hidden">
            {currentMatch.photo_url ? (
              <img 
                src={currentMatch.photo_url} 
                alt={currentMatch.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-32 h-32 sm:w-36 sm:h-36 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-white text-4xl sm:text-5xl font-bold">
                  {currentMatch.name.charAt(0)}
                </span>
              </div>
            )}
            
            {/* Compatibility Score */}
            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center space-x-1">
              <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-yellow-500" />
              <span className="text-xs font-semibold">{currentMatch.compatibility}%</span>
            </div>
          </div>
          
          {/* Info - Balanced spacing */}
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{currentMatch.name}</h2>
              <span className="text-base sm:text-lg text-gray-600">Age {currentMatch.age}</span>
            </div>
            
            <div className="flex items-center text-gray-600 mb-2">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5" />
              <span className="text-sm sm:text-base">{currentMatch.distance} miles away</span>
            </div>
            
            <p className="text-sm sm:text-base text-gray-700 mb-3 line-clamp-2">{currentMatch.bio}</p>
            
            {/* Interests */}
            <div className="flex flex-wrap gap-2 mb-2">
              {currentMatch.interests.slice(0, 3).map((interest, index) => (
                <span key={index} className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-sm">
                  {interest}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Action Buttons - Larger on mobile */}
        <div className="flex items-center justify-center space-x-8 mt-4 sm:mt-5">
          <button
            onClick={handlePass}
            className="w-16 h-16 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <X className="h-8 w-8 sm:h-8 sm:w-8 text-gray-500" />
          </button>
          
          <button
            onClick={() => handleLike(currentMatch.id)}
            className="w-20 h-20 sm:w-20 sm:h-20 bg-gradient-to-r from-rose-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="h-10 w-10 sm:h-10 sm:w-10 text-white" />
          </button>
        </div>
        
        {/* Progress - Compact spacing */}
        <div className="text-center mt-3 mb-3">
          <p className="text-white font-semibold text-sm mb-1.5">
            {currentMatchIndex + 1} of {potentialMatches.length} singles
          </p>
          <button
            onClick={() => navigate('/connections')}
            className="bg-white/20 backdrop-blur-sm text-white px-6 py-2.5 rounded-full hover:bg-white/30 text-sm font-semibold transition-all shadow-lg"
          >
            💚 View My Connections ({userMatches.length})
          </button>
        </div>
      </div>
      
      {/* Upgrade Modal - render at root level with high z-index */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[9999]">
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            feature="chat"
            message="Upgrade to Premium or VIP to chat with your matches!"
          />
        </div>
      )}
    </div>
  );
};

export default MatchingInterface;